<script setup lang="ts">
import { watch, toRefs } from "vue";
import TextField from "@/shared/components/TextField/TextField.vue";
import PasswordField from "@/shared/components/PasswordField/PasswordField.vue";
import Button from "@/shared/components/Button/Button.vue";
import SelectField from "@/shared/components/SelectField/SelectField.vue";
import Form from "@/shared/components/Form/Form.vue";
import PermissionChecks from "@/shared/components/PermissionChecks/PermissionChecks.vue";
import Title from "@/shared/components/Title/Title.vue";
import { RegistrationType } from "@/shared/generated/graphql";
import { TEXTFIELD_MAX_LENGTH_DEFAULT } from "@/shared/helper/constants.helper";
import type { RegistrationFormData } from "@/app/registration-app/user/invited-registration/@types/RegistrationFormData";
import type { InitialRegistrationData } from "@/app/registration-app/user/invited-registration/@types/InitialRegistrationData";
import { useRegistrationDataFields } from "./useRegistrationDataFields.composable";

const props = withDefaults(
  defineProps<{
    initialValues: InitialRegistrationData | undefined;
    registrationType: RegistrationType;
    finishedRegistration: boolean;
    isLoading?: boolean;
  }>(),
  {
    isLoading: false,
  },
);

const { initialValues, registrationType } = toRefs(props);

const emit = defineEmits<{
  (e: "register", fields: RegistrationFormData): void;
}>();

// --- Get Form-Fields ---
const {
  salutation: { value: salutation, errors: salutationErrors },
  salutationOptions,
  firstName: { value: firstName, errors: firstNameErrors },
  lastName: { value: lastName, errors: lastNameErrors },
  password: { value: password, errors: passwordErrors, meta: passwordMeta },
  possiblePasswordErrors,
  email: { value: email, errors: emailErrors },
  conditions: { value: conditions, errors: conditionsErrors },
  privacy: { value: privacy, errors: privacyErrors },
  newsletter: { value: newsletter, errors: newsletterErrors },
  handleSubmit,
  setInitialRegistrationData,
} = useRegistrationDataFields(registrationType, props.finishedRegistration);

watch(
  initialValues,
  () => {
    if (initialValues.value) {
      setInitialRegistrationData(initialValues.value);
    }
  },
  { immediate: true },
);

const onSubmit = handleSubmit((fields: RegistrationFormData) =>
  emit("register", fields),
);
</script>

<template>
  <div class="registration-form">
    <Title
      size="l"
      :text="$t('registration.user.invited.title')"
    />
    <div class="registration-form--text">
      <p>
        {{ $t("registration.user.invited.info") }}
      </p>
      <p>
        {{ $t("registration.user.invited.info2") }}
      </p>
    </div>
    <div class="registration-view--form">
      <Form @submit="onSubmit">
        <template #default>
          <SelectField
            v-show="!initialValues?.salutation || !finishedRegistration"
            v-model="salutation"
            :options="salutationOptions"
            :errors="salutationErrors"
            :label="$t('shared.address.salutation')"
          />
          <TextField
            v-show="!finishedRegistration"
            v-model="firstName"
            :errors="firstNameErrors"
            :label="$t('shared.address.firstName')"
            :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
            autocomplete="given-name"
          />
          <TextField
            v-show="!finishedRegistration"
            v-model="lastName"
            :errors="lastNameErrors"
            :label="$t('shared.address.lastName')"
            :maxlength="TEXTFIELD_MAX_LENGTH_DEFAULT"
            autocomplete="family-name"
          />
          <TextField
            v-model="email"
            :errors="emailErrors"
            :disabled="registrationType === RegistrationType.Email"
            :label="$t('shared.term.email.short')"
            :hint="
              registrationType === RegistrationType.Email
                ? $t('registration.user.invited.emailInfo')
                : undefined
            "
            autocomplete="email"
          />
          <PasswordField
            v-show="registrationType === RegistrationType.Email"
            v-model="password"
            :errors="passwordErrors"
            :possible-errors="possiblePasswordErrors"
            show-rules
            :show-as-validated="passwordMeta.validated"
            :label="$t('shared.term.password.default')"
          />
          <div class="registration-form--permissions">
            <PermissionChecks
              v-model:conditions="conditions"
              v-model:privacy="privacy"
              v-model:newsletter="newsletter"
              :conditions-errors="conditionsErrors"
              :privacy-errors="privacyErrors"
              :newsletter-errors="newsletterErrors"
              :has-newsletter-checkbox="
                registrationType === RegistrationType.Email
              "
              :hide-checks="finishedRegistration"
            />
          </div>
        </template>
        <template #actions>
          <div class="registrations-form--actions">
            <Button
              type="submit"
              :caption="$t('registration.shared.action.register')"
              :is-loading="isLoading"
            />
          </div>
        </template>
      </Form>
    </div>
  </div>
</template>

<style scoped>
@import "RegistrationForm.css";
</style>
